import React from "react";
import { Box, Text, Stack } from "@bookingcom/bui-react";
import { trackWithDefaultStage } from "@flights/et-universal";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import * as etV2 from "@flights/et-universal";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";

import Container from "../Container";
import NoJS from "../../../screens/NoJS";
import useSearchbox from "../../../hooks/useSearchbox";
import useLocaleContext from "../../../hooks/useLocaleContext";
import useLandingPageEvents from "../../../hooks/c360/useLandingPageEvents";

import styles from "./HomeSearchHeader.desktop.module.css";
import { BLACK_FRIDAY_IMAGE_URL, BLACK_FRIDAY_BG_COLOR, BLACK_FRIDAY_EXLUDED_COUNTRIES } from "../../../constants";
import { mcn } from "../../../utils/mergeClassnames";
import { BlackFridayCountdown } from "../BlackFridayBanner";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { isOfMetaOrigin, isOfPPCOrigin } from "../../../utils/marketing-url-params";

const HomeSearchHeader = () => {
  const i18n = useI18n();
  const searchBoxProps = useSearchbox();
  const { isRTL } = useLocaleContext();
  const trackLandingPageEvents = useLandingPageEvents();
  const { ipCountry = "" } = useGlobalContext();

  const isResponsiveFixes = !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  const isBlackFridayExp =
    !isOfPPCOrigin() &&
    !isOfMetaOrigin() &&
    !BLACK_FRIDAY_EXLUDED_COUNTRIES.includes(ipCountry.toLowerCase()) &&
    !!trackWithDefaultStage("flights_web_cat_black_friday_index_2025", 1);

  const boxStyles = mcn(styles.titleBox, isBlackFridayExp && styles.titleBoxBlackFriday);
  const titleStackStyles = mcn(styles.titleStack, isBlackFridayExp && styles.titleStackBlackFriday);
  const titleStyles = mcn(styles.title, isBlackFridayExp && styles.titleBlackFriday);
  const subtitleStyles = mcn(styles.subtitle, isBlackFridayExp && styles.subtitleBlackFriday);

  return (
    <>
      <Box className={boxStyles}>
        <Container
          className={styles.titleContainer}
          style={{ backgroundColor: isBlackFridayExp ? `${BLACK_FRIDAY_BG_COLOR}` : undefined }}
        >
          <NoJS />
          <Stack
            gap={1}
            className={titleStackStyles}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack.Item grow className={styles.titleStackItem}>
              {isBlackFridayExp ? <BlackFridayCountdown /> : null}
              <Text variant="display_3" data-testid="home_header" tagName="h1" color="white" className={titleStyles}>
                {isBlackFridayExp
                  ? i18n.trans(t("flights_ddot_fly_anywhere_blackfriday_title"))
                  : i18n.trans(t("flights_index_header_compare_book_ease"))}
              </Text>
              <Text variant="featured_2" color="white" className={subtitleStyles}>
                {isBlackFridayExp
                  ? i18n.trans(t("flights_ddot_hero_blackfriday25_subtitle"))
                  : i18n.trans(t("flights_index_header_discover_next_dream"))}
              </Text>
            </Stack.Item>
            {isBlackFridayExp ? (
              <div
                role="img"
                className={styles.blackFridayImage}
                style={{
                  backgroundImage: `url(${BLACK_FRIDAY_IMAGE_URL})`
                }}
                aria-label={i18n.trans(t("a11y_flights_blackfriday25_hero_image"))}
              />
            ) : null}
          </Stack>
        </Container>
      </Box>

      <Box className={`${styles.searchboxControllerBox} ${isResponsiveFixes ? styles.searchboxControllerBoxRes : ""}`}>
        <Container
          className={`${
            isResponsiveFixes ? styles.searchBoxControllerContainerRes : styles.searchBoxControllerContainer
          }`}
        >
          <SearchboxController
            i18n={i18n}
            {...searchBoxProps}
            hideShadow={true}
            isRTL={isRTL}
            isHeaderUpdateExp={true}
            isResponsiveFixes={isResponsiveFixes}
            enabledCountriesAndAnywhere={true}
            isEmptyDestinationExp={!!trackWithDefaultStage("flights_web_cat_empty_destination_www", 1)}
            isBlackFridayExp={isBlackFridayExp}
            onSearch={(searchParams) => {
              trackLandingPageEvents("click", "searchbox");
              etV2.customGoal("mvsf_whs_launchpad_stays_flights_www", 2);
              searchBoxProps.onSearch(searchParams);
            }}
          >
            {SearchBoxHorizontalDefault}
          </SearchboxController>
        </Container>
      </Box>
    </>
  );
};

export default HomeSearchHeader;
